/**
 * The CV share flow's neutral product chrome. The colourful theme belongs
 * only to the share card preview. Everything around it (the Share sheet, the
 * share editor and the row sheets they open) uses one quiet palette, the
 * design repo's light tokens (share-refinement.css, 2026-10-05): sheet
 * #ffffff, text #1d1d1f, secondary #56565e, line #e8e8ec. Light mode only;
 * dark mode keeps the app's sheet tokens.
 */
import type { ReactNode } from 'react';
import {
  SheetPaletteContext,
  type SheetPaletteOverride,
} from '../common/sheet-palette';
import { useTheme } from '../../hooks/useTheme';

export const SHARE_SHEET_COLORS = {
  sheet: '#ffffff',
  card: '#ffffff',
  border: '#e8e8ec',
  text: '#1d1d1f',
  textSecondary: '#56565e',
  /** The sheet's primary button (design `.primary`). */
  primary: '#202022',
} as const;

/** A selected picker tile (design `.preview-choice-grid button[aria-pressed=true]`). */
export const SHARE_SHEET_CHOICE = {
  border: '#7657f6',
  text: '#5529be',
  background: '#f5f2ff',
  /** Dark-mode twins: the same violet on a translucent field. */
  darkText: '#cdbfff',
  darkBackground: 'rgba(118, 87, 246, 0.18)',
} as const;

const SHARE_SHEET_LIGHT_PALETTE: SheetPaletteOverride = {
  pageBg: SHARE_SHEET_COLORS.sheet,
  text: SHARE_SHEET_COLORS.text,
  textSecondary: SHARE_SHEET_COLORS.textSecondary,
  textTertiary: SHARE_SHEET_COLORS.textSecondary,
  inputBg: SHARE_SHEET_COLORS.card,
  inputBorder: SHARE_SHEET_COLORS.border,
  hairline: SHARE_SHEET_COLORS.border,
};

/** Wrap a share-flow surface so its sheets and tokens use the palette. */
export function ShareSheetPalette({ children }: { children: ReactNode }) {
  const { isDark } = useTheme();
  return (
    <SheetPaletteContext.Provider value={isDark ? null : SHARE_SHEET_LIGHT_PALETTE}>
      {children}
    </SheetPaletteContext.Provider>
  );
}
